import React, {
  ComponentPropsWithoutRef,
  FunctionComponent,
  useEffect,
  useState
} from 'react'
import FeedbackMessage, {
  FeedbackMessageProps
} from '@ta-interaktiv/react-feedback-message'
import { ShareButtons } from '@ta-interaktiv/react-share-buttons'
import {
  CopyrightNotice,
  CopyrightNoticeProps
} from '@ta-interaktiv/react-copyright-notice'
import {
  UserFeedback,
  UserFeedbackProps
} from '@ta-interaktiv/react-user-feedback'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/table.css'
import styles from './footer.module.scss'
import { PortfolioTeaser } from './portfolioTeaser'
import frenchMessages from './locales/fr.yml'
import germanMessages from './locales/de.yml'
import englishMessages from './locales/en.yml'
import { ArticleId, Locale, SupportedLanguage } from '@ta-interaktiv/types'
import { ScrollTracking } from './scrollTracking'
import { getLanguage, getPublishingDate } from '@ta-interaktiv/api-access'

const messages = {
  de: germanMessages,
  fr: frenchMessages,
  en: englishMessages
}

const defaultPublishingDate = new Date(
  (window.project?.buildTimestamp || 0) * 1000
).toISOString()

type SelectedUserFeedbackProps = Partial<UserFeedbackProps>

type ShareButtonProps = Pick<
  ComponentPropsWithoutRef<typeof ShareButtons>,
  'additionalShares'
>

export interface FooterProps extends ArticleId, Locale {
  /**
   * A table of credits: rows containing arrays that each contain the title
   * and the credited person(s). You can add HTML code to the strings you
   * pass in here,
   * they will be
   * dangerously set into the cells. Useful if you need to add additional
   * links to sources.
   */
  credits: [string, string][]

  /** Hide the portfolio teaser, if necessary */
  hidePortfolioTeaser?: boolean

  /** Hide user feedback, if necessary. */
  hideUserFeedback?: boolean

  /** Hide the top part of the footer component. */
  hideTop?: boolean

  /** Hide the bottom part of the footer component. */
  hideBottom?: boolean

  /** Class names for the top half of the footer */
  topHalfClassName?: string

  /** Classes for the bottom half of the footer */
  bottomHalfClassName?: string

  /** Should the top half be inverted? */
  invertedTop?: boolean

  /** Should the bottom half be inverted? */
  invertedBottom?: boolean

  children: React.ReactNode
}

/**
 * Meta-Component that displays a footer for use in Tamedia interactive standalones.
 *
 * ## Installation
 * ```bash
 * yarn add @ta-interaktiv/react-footer
 * ```
 *
 * ## Basic Usage
 * ```jsx
 * import React from 'react'
 * import { Footer } from '@ta-interaktiv/react-footer'
 *
 *
 * const function Component() {
 *   return (
 *     <Footer
 *        articleId='00000000'
 *        credits={[
 *          ['Text', 'Mark Twain'],
 *          ['Programmierung', 'Alan Turing']
 *        ]}
 *     />
 *   )
 * }
 * ```
 *
 */
export const Footer: FunctionComponent<FooterProps &
  CopyrightNoticeProps &
  FeedbackMessageProps &
  SelectedUserFeedbackProps &
  ShareButtonProps> = ({
  additionalShares,
  articleId,
  call,
  credits,
  displayPrivacyPolicyLink,
  fullMediaName,
  inverted,
  locale,
  mailTo,
  mediaName,
  project,
  question,
  questions,
  starColor,
  title,
  hidePortfolioTeaser = false,
  hideUserFeedback = false,
  hideBottom = false,
  hideTop = false,
  year,
  topHalfClassName = '',
  bottomHalfClassName = 'grey',
  children,
  invertedTop,
  invertedBottom
}) => {
  /**
   * The domain is used to download stuff from the API.
   *
   * In the case of dasmagazin.ch or sonntagszeitung.ch, there is either no
   *  API, or the API falsely reports that access to the article is denied.
   *  So we fall back to the domain name that's currently being used.
   */
  const domain =
    mediaName === 'sonntagszeitung' || mediaName === 'dasmagazin'
      ? 'tagesanzeiger'
      : mediaName || parseHostname().publicationName

  const [dynamicLocale, setLocale] = useState<'de' | 'fr'>(
    (window.document.documentElement
      .getAttribute('lang')
      ?.split('-')[0] as SupportedLanguage) || 'de'
  )
  const [publicationDate, setPublicationDate] = useState<string | undefined>(
    defaultPublishingDate
  )

  // Get data from external_services endpoint
  useEffect(() => {
    getLanguage(domain).then(lang => {
      if (lang) setLocale(lang)
    })
  }, [domain])

  // Get data from article endpoint
  useEffect(() => {
    getPublishingDate(domain, articleId).then(pubDate => {
      setPublicationDate(pubDate)
    })
  }, [articleId, domain])

  // Redefined locale
  locale = locale || dynamicLocale

  // Set up props for dependent components
  const copyrightNoticeProps: CopyrightNoticeProps = {
    displayPrivacyPolicyLink,
    fullMediaName,
    inverted: inverted || invertedBottom,
    locale,
    mediaName,
    year
  }

  const portfolioTeaserProps = {
    mediaName
  }

  const feedbackMessageProps: Partial<FeedbackMessageProps> = {
    call,
    inverted: inverted || invertedTop,
    locale,
    mailTo,
    project,
    question
  }

  const shareButtonProps: ComponentPropsWithoutRef<typeof ShareButtons> = {
    articleId,
    additionalShares
  }

  const userFeedbackProps = {
    articleId,
    inverted: inverted || invertedTop,
    locale,
    questions,
    title,
    starColor
  }

  let sharingTitleClasses = `ui sub header ${styles.sharingTitle}`
  sharingTitleClasses += inverted ? ' inverted' : ''

  // Set up classes for inverted style
  const topHalfClasses =
    inverted || invertedTop
      ? `inverted ${styles.inverted} ${topHalfClassName}`
      : topHalfClassName
  const bottomHalfClasses =
    inverted || invertedBottom
      ? `inverted ${styles.inverted} ${bottomHalfClassName}`
      : bottomHalfClassName
  const tableClass = inverted || invertedBottom ? 'inverted' : ''

  // Set up date formatter
  const dateTimeFormatter = new Intl.DateTimeFormat(locale + '-CH', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
  })

  return (
    <footer className={`standalone-footer ${styles.footer}`}>
      <ScrollTracking eventLabel='contentBottom' />
      {!hideTop && (
        <div className={`ui vertical segment topHalf ${topHalfClasses}`}>
          <div className='ui text infographic container'>
            <aside
              className={`ui vertical very fitted basic segment ${styles.sharing}`}
            >
              <div className={sharingTitleClasses}>
                {messages[locale].sharingTitle}:
              </div>
              <ShareButtons {...shareButtonProps} />
            </aside>

            {!hideUserFeedback && (
              <aside className='ui vertical very fitted basic segment'>
                <UserFeedback {...userFeedbackProps} />
              </aside>
            )}

            <FeedbackMessage {...feedbackMessageProps} />
          </div>
        </div>
      )}
      {children}
      {!hideBottom && (
        <div
          className={`ui vertical secondary segment bottomHalf ${bottomHalfClasses}`}
        >
          <div className='ui text container'>
            {!hidePortfolioTeaser && (
              <PortfolioTeaser {...portfolioTeaserProps} />
            )}

            <table
              className={`ui very basic fixed ${tableClass} definition infographic table ${styles.creditsTable}`}
            >
              <tbody>
                {credits.map((row: [string, string], rowIndex: number) => (
                  <tr key={`credits-${rowIndex}`} className='top aligned'>
                    {row.map((cell, cellIndex) => (
                      <td
                        className='eight wide'
                        key={`credits-${rowIndex}-${cellIndex}`}
                        dangerouslySetInnerHTML={{ __html: cell }}
                      />
                    ))}
                  </tr>
                ))}
                {publicationDate && (
                  <tr>
                    <td>{messages[locale].published}</td>
                    <td>
                      {dateTimeFormatter.format(new Date(publicationDate))}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <CopyrightNotice {...copyrightNoticeProps} />
          </div>
        </div>
      )}
      <ScrollTracking eventLabel='pageBottom' />
    </footer>
  )
}
